import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Heart, Sparkles, ArrowRight, User, Upload, X, Camera, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import heroImage from "@/assets/hero-image.jpg";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { AvatarCropDialog } from "@/components/AvatarCropDialog";
import { displayNameSchema } from "@/lib/validationSchemas";

const journeyStages = [
  { value: "exploring", label: "Just Exploring", description: "I'm starting to notice changes and want to learn more" },
  { value: "perimenopause", label: "Perimenopause", description: "I'm experiencing irregular periods and symptoms" },
  { value: "menopause", label: "Menopause", description: "It's been 12+ months since my last period" },
  { value: "postmenopause", label: "Postmenopause", description: "I'm navigating life after menopause" },
  { value: "unsure", label: "Not Sure", description: "I'm figuring it out as I go" }
];

const commonSymptoms = [
  "Hot Flashes",
  "Night Sweats",
  "Sleep Issues",
  "Mood Changes",
  "Brain Fog",
  "Anxiety",
  "Fatigue",
  "Weight Changes",
  "Joint Pain",
  "Headaches"
];

const commonGoals = [
  "Better Sleep",
  "Manage Hot Flashes",
  "Improve Mood",
  "Increase Energy",
  "Maintain Weight",
  "Reduce Anxiety",
  "Better Focus",
  "Stay Active",
  "Connect with Others",
  "Learn More"
];

export default function ProfileSetup() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  
  const [displayName, setDisplayName] = useState("");
  const [journeyStage, setJourneyStage] = useState("");
  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>([]);
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is authenticated
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Session Expired",
          description: "Please log in again to continue",
          variant: "destructive"
        });
        navigate("/auth");
      }
    };
    checkAuth();
  }, [navigate, toast]);

  const handleSymptomToggle = (symptom: string) => {
    setSelectedSymptoms(prev =>
      prev.includes(symptom)
        ? prev.filter(s => s !== symptom)
        : [...prev, symptom]
    );
  };

  const handleGoalToggle = (goal: string) => {
    setSelectedGoals(prev =>
      prev.includes(goal)
        ? prev.filter(g => g !== goal)
        : [...prev, goal]
    );
  };

  const handleAvatarSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Avatar must be less than 5MB",
        variant: "destructive"
      });
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file (JPG, PNG, GIF, or WebP)",
        variant: "destructive"
      });
      return;
    }

    const imageSrc = URL.createObjectURL(file);
    setSelectedImageSrc(imageSrc);
    setCropDialogOpen(true);
    e.target.value = '';
  };

  const handleCropComplete = async (croppedImage: Blob) => {
    setUploading(true);

    // Retry helper for network resilience
    const retryUpload = async (fn: () => Promise<any>, maxRetries = 3): Promise<any> => {
      for (let i = 0; i < maxRetries; i++) {
        try {
          return await fn();
        } catch (error: any) {
          const isNetworkError = error.message?.includes('fetch') || 
                                 error.message?.includes('network') ||
                                 error.message?.includes('Failed to fetch');
          if (i === maxRetries - 1 || !isNetworkError) throw error;
          await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
        }
      }
    };

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error("Authentication required. Please sign in again.");

      const fileName = `${session.user.id}/${Date.now()}.jpg`;

      const { error: uploadError } = await retryUpload(async () => {
        return supabase.storage
          .from('avatars')
          .upload(fileName, croppedImage, { 
            upsert: true,
            contentType: 'image/jpeg'
          });
      });

      if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      setAvatarUrl(publicUrl);

      toast({
        title: "Avatar uploaded successfully! ✨",
        description: "Looking good! Complete setup to save your avatar."
      });
    } catch (error: any) {
      const isNetworkError = error.message?.includes('fetch') || error.message?.includes('network');
      toast({
        title: "Upload failed",
        description: isNetworkError 
          ? "Network connection issue. Please check your internet and try again."
          : (error.message || "Failed to upload avatar. Please try again."),
        variant: "destructive"
      });
    } finally {
      setUploading(false);
      if (selectedImageSrc) {
        URL.revokeObjectURL(selectedImageSrc);
        setSelectedImageSrc(null);
      }
    }
  };

  const handleRemoveAvatar = () => {
    setAvatarUrl(null);
    toast({
      title: "Avatar removed",
      description: "You can upload a new one anytime"
    });
  };

  const handleNext = () => {
    if (step === 1) {
      // Validate display name
      const validation = displayNameSchema.safeParse(displayName);
      if (!validation.success) {
        toast({
          title: "Invalid Name",
          description: validation.error.errors[0].message,
          variant: "destructive"
        });
        return;
      }
    }
    if (step === 2 && !journeyStage) {
      toast({
        title: "Journey Stage Required",
        description: "Please select where you are in your journey",
        variant: "destructive"
      });
      return;
    }
    setStep(prev => prev + 1);
  };

  const handleSubmit = async () => {
    /**
     * USER JOURNEY: Profile Setup Completion
     * 
     * This is the primary onboarding flow for new users:
     * /auth (signup) → /profile-setup → /pricing (start trial with card)
     * 
     * After completing profile setup, users are redirected to pricing
     * to enter their credit card and start their 14-day free trial.
     */
    setLoading(true);

    // Retry helper for network resilience
    const retryOperation = async <T,>(fn: () => Promise<T>, maxRetries = 3): Promise<T> => {
      for (let i = 0; i < maxRetries; i++) {
        try {
          return await fn();
        } catch (error: any) {
          const isNetworkError = error.message?.includes('fetch') || 
                                 error.message?.includes('network') ||
                                 error.message?.includes('Failed to fetch');
          if (i === maxRetries - 1 || !isNetworkError) throw error;
          await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
        }
      }
      throw new Error('Operation failed after retries');
    };

    try {
      const { data: { session } } = await retryOperation(() => supabase.auth.getSession());
      
      if (!session?.user) {
        toast({
          title: "Session Expired",
          description: "Please log in again to continue",
          variant: "destructive"
        });
        navigate("/auth");
        return;
      }

      // Set 5-day free exploration period
      const freeAccessEndsAt = new Date();
      freeAccessEndsAt.setDate(freeAccessEndsAt.getDate() + 5);

      const result = await retryOperation(async () => {
        return await supabase
          .from("profiles")
          .upsert({
            user_id: session.user.id,
            display_name: displayName,
            journey_stage: journeyStage,
            symptoms: selectedSymptoms,
            goals: selectedGoals,
            avatar_url: avatarUrl,
            onboarding_completed: true,
            free_access_ends_at: freeAccessEndsAt.toISOString(),
            has_started_trial: false,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'user_id'
          });
      });

      if (result.error) throw new Error(`Failed to save profile: ${result.error.message}`);

      toast({
        title: "Welcome! 🎉",
        description: "You have 5 days of free access to explore all features"
      });

      // Redirect to home to start exploring
      navigate("/");
    } catch (error: any) {
      const isNetworkError = error.message?.includes('fetch') || error.message?.includes('network');
      toast({
        title: "Error",
        description: isNetworkError 
          ? "Network connection issue. Please check your internet and try again."
          : error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(135deg, rgba(145, 25, 55, 0.85), rgba(175, 30, 50, 0.75)), url(${heroImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-primary/30 to-secondary/30 rounded-full blur-3xl opacity-50" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-accent/30 to-primary/30 rounded-full blur-3xl opacity-50" />

      <div className="relative z-10 container mx-auto px-4 py-12 min-h-screen flex items-center justify-center">
        <Card className="max-w-2xl w-full bg-background/95 backdrop-blur-xl shadow-2xl border-2 border-primary/20 animate-fade-in">
          <CardHeader className="text-center pb-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary mb-4 mx-auto shadow-lg">
              {step === 1 ? <User className="h-8 w-8 text-white" /> : 
               step === 2 ? <Heart className="h-8 w-8 text-white" /> :
               <Sparkles className="h-8 w-8 text-white" />}
            </div>
            <CardTitle className="text-3xl font-bold">
              {step === 1 && "Let's Get to Know You"}
              {step === 2 && "Your Journey"}
              {step === 3 && "What You're Experiencing"}
              {step === 4 && "Your Goals"}
            </CardTitle>
            <p className="text-muted-foreground mt-2">
              {step === 1 && "What should we call you? This is just between us."}
              {step === 2 && "Everyone's journey is unique. Where are you right now?"}
              {step === 3 && "Select any symptoms you're dealing with (optional)"}
              {step === 4 && "What would you like to work on? (optional)"}
            </p>
            
            {/* Progress Indicator */}
            <div className="flex gap-2 justify-center mt-6">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className={`h-2 rounded-full transition-all ${
                    i === step ? 'w-8 bg-primary' : 
                    i < step ? 'w-2 bg-primary/50' : 
                    'w-2 bg-muted'
                  }`}
                />
              ))}
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Step 1: Display Name & Avatar */}
            {step === 1 && (
              <div className="space-y-6 animate-fade-in">
                {/* Avatar Upload */}
                <div className="space-y-3">
                  <Label className="text-base flex items-center gap-2">
                    <Camera className="h-5 w-5 text-primary" />
                    Profile Picture (Optional)
                  </Label>
                  <div className="flex flex-col items-center gap-4">
                    <Avatar className="h-32 w-32 border-4 border-primary/20 shadow-lg">
                      <AvatarImage src={avatarUrl || undefined} />
                      <AvatarFallback className="text-4xl bg-gradient-to-br from-primary/10 to-secondary/10">
                        {displayName.charAt(0).toUpperCase() || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col items-center gap-2">
                      <Label htmlFor="avatar-upload" className="cursor-pointer">
                        <Button
                          type="button"
                          variant="outline"
                          disabled={uploading}
                          className="gap-2"
                          onClick={() => document.getElementById('avatar-upload')?.click()}
                        >
                          <Upload className="h-4 w-4" />
                          {uploading ? "Uploading..." : avatarUrl ? "Change Avatar" : "Upload Avatar"}
                        </Button>
                      </Label>
                      <input
                        id="avatar-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarSelect}
                        className="hidden"
                      />
                      {avatarUrl && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleRemoveAvatar}
                          className="gap-2"
                        >
                          <X className="h-4 w-4" />
                          Remove
                        </Button>
                      )}
                      <p className="text-xs text-center text-muted-foreground max-w-xs">
                        JPG, PNG, GIF or WebP. Max 5MB. Image will be cropped to 512x512 pixels.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Display Name */}
                <div>
                  <Label htmlFor="displayName" className="text-base mb-2 block">
                    Your Name (or nickname)
                  </Label>
                  <Input
                    id="displayName"
                    placeholder="What should we call you?"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="text-lg p-6"
                    autoFocus
                  />
                  <p className="text-sm text-muted-foreground mt-2">
                    Don't worry, you can stay anonymous in the community if you prefer
                  </p>
                </div>
              </div>
            )}

            {/* Step 2: Journey Stage */}
            {step === 2 && (
              <div className="space-y-4 animate-fade-in">
                <RadioGroup value={journeyStage} onValueChange={setJourneyStage}>
                  {journeyStages.map((stage) => (
                    <Card
                      key={stage.value}
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        journeyStage === stage.value ? 'border-2 border-primary bg-primary/5' : 'border'
                      }`}
                      onClick={() => setJourneyStage(stage.value)}
                    >
                      <CardContent className="flex items-start gap-4 p-4">
                        <RadioGroupItem value={stage.value} id={stage.value} className="mt-1" />
                        <div className="flex-1">
                          <Label htmlFor={stage.value} className="text-base font-semibold cursor-pointer">
                            {stage.label}
                          </Label>
                          <p className="text-sm text-muted-foreground mt-1">
                            {stage.description}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </RadioGroup>
              </div>
            )}

            {/* Step 3: Symptoms */}
            {step === 3 && (
              <div className="space-y-4 animate-fade-in">
                <p className="text-sm text-muted-foreground">
                  This helps us personalize your experience. Select all that apply:
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {commonSymptoms.map((symptom) => (
                    <Card
                      key={symptom}
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        selectedSymptoms.includes(symptom) ? 'border-2 border-primary bg-primary/5' : 'border'
                      }`}
                      onClick={() => handleSymptomToggle(symptom)}
                    >
                      <CardContent className="flex items-center gap-3 p-4">
                        <Checkbox
                          checked={selectedSymptoms.includes(symptom)}
                          onCheckedChange={() => handleSymptomToggle(symptom)}
                        />
                        <span className="text-sm font-medium">{symptom}</span>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Step 4: Goals */}
            {step === 4 && (
              <div className="space-y-4 animate-fade-in">
                <p className="text-sm text-muted-foreground">
                  What would you like to focus on? Select your top priorities:
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {commonGoals.map((goal) => (
                    <Card
                      key={goal}
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        selectedGoals.includes(goal) ? 'border-2 border-primary bg-primary/5' : 'border'
                      }`}
                      onClick={() => handleGoalToggle(goal)}
                    >
                      <CardContent className="flex items-center gap-3 p-4">
                        <Checkbox
                          checked={selectedGoals.includes(goal)}
                          onCheckedChange={() => handleGoalToggle(goal)}
                        />
                        <span className="text-sm font-medium">{goal}</span>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-4 pt-6">
              {step > 1 && (
                <Button
                  variant="outline"
                  onClick={() => setStep(prev => prev - 1)}
                  className="flex-1"
                  disabled={loading}
                >
                  Back
                </Button>
              )}
              
              {step < 4 ? (
                <Button
                  onClick={handleNext}
                  className="flex-1 gap-2"
                  disabled={loading || uploading}
                >
                  Continue
                  <ArrowRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  className="flex-1 gap-2"
                  disabled={loading || uploading}
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {loading ? "Setting Up..." : "Complete Setup"}
                  {!loading && <Heart className="h-4 w-4" />}
                </Button>
              )}
            </div>

            {step > 2 && (
              <p className="text-center text-sm text-muted-foreground">
                You can always update this later in your profile
              </p>
            )}
          </CardContent>
        </Card>

        {selectedImageSrc && (
          <AvatarCropDialog
            open={cropDialogOpen}
            onOpenChange={(open) => {
              setCropDialogOpen(open);
              if (!open && selectedImageSrc) {
                URL.revokeObjectURL(selectedImageSrc);
                setSelectedImageSrc(null);
              }
            }}
            imageSrc={selectedImageSrc}
            onCropComplete={handleCropComplete}
          />
        )}
      </div>
    </div>
  );
}
